import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Images } from "lucide-react";
import { MobilePageTitle } from "../components/MobilePageTitle";
import { MODES, VIDEO_INFLUENCER_ENABLED } from "./v2/modesCatalog";
import { getModeExamples, type ModeExample } from "./v2/modeExamples";
import { ModeExamplesModal } from "./v2/components/ModeExamplesModal";
import { setupAutoplayVideo, ModeLockOverlay } from "./v2/components/HomeCards";
import { getUserPlan } from "../lib/planUtils";
import {
  getModePlanLock,
  getPlanRequirementNote,
  type ModePlanLock,
} from "../lib/planCapabilities";
import { FeatureLockedModal } from "../components/FeatureLockedModal";
import { EstudioVideoCover } from "../components/EstudioVideoCover";
import { DesktopOnlyModal } from "../components/DesktopOnlyModal";
import { useUserGroup } from "../hooks/useUserGroup";
import { useRubroMarcaActiva } from "../hooks/useRubroMarca";
import { esRubroHogarDeco } from "../lib/rubro";

/**
 * Pantalla "Modos" — lista los modos de generación de Interfaz V2 (los mismos
 * del design-row de la home), uno abajo del otro, cada uno con su video,
 * título y descripción. Al tocar uno se entra a ese modo en V2.
 */
export const Modos = () => {
  const navigate = useNavigate();
  // Modos `devOnly` (ej. "Video con influencer") solo se listan para dev testers.
  const { isDevTester } = useUserGroup();
  // Modos `soloHogarDeco` (catálogo de muebles + infografías): se listan solo si
  // la MARCA activa es del rubro "Hogar y Decoración". Mientras el rubro no se
  // conoce (`null`), NO se muestran — el cache de localStorage del hook evita
  // que aparezcan un frame tarde para quien sí califica.
  const { rubro: rubroMarca } = useRubroMarcaActiva();
  const esHogarDeco = esRubroHogarDeco(rubroMarca);

  // Plan del usuario para gatear el acceso a los modos. Arrancamos del cache
  // de localStorage (mismo que llena el Sidebar) para tener el plan al instante
  // y refrescamos con el valor real. Si todavía no se conoce, no bloqueamos.
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem("atlas-user-plan");
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelled = false;
    getUserPlan().then((p) => {
      if (!cancelled && p) setPlan(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  // Lock a mostrar cuando el plan no alcanza para el modo tocado.
  const [modeLock, setModeLock] = useState<ModePlanLock | null>(null);

  // Visor de ejemplos ("Ver ejemplos"): modo + su lista, o null si cerrado.
  // CTA del visor: "Generar" (entra al modo) o, si el modo está bloqueado
  // para el plan, "Mejorar plan" (navega a /subscription) + una nota de qué
  // plan requiere. Se calcula al abrir.
  const [examplesModal, setExamplesModal] = useState<{
    title: string;
    description?: string;
    examples: ModeExample[];
    actionLabel: string;
    onAction: () => void;
    locked: boolean;
    planNote?: string;
  } | null>(null);

  // Nota del plan requerido: compartida con la home V2 (planCapabilities).
  const planRequirementNote = getPlanRequirementNote;

  // Card "Estudio de Video" (editor pro): Ultra-only. Los dev testers lo ven
  // desbloqueado (igual que en el Sidebar). Si el plan no alcanza → candado +
  // modal compartidos. Ver ADR-526.
  //
  // Solo bloqueamos cuando sabemos con CERTEZA que no es dev tester
  // (isDevTester === false). Mientras el hook no resolvió (null), NO bloqueamos
  // — mismo criterio que getModePlanLock con el plan desconocido: así un dev
  // tester no ve un flash de candado en el primer login (antes de que cargue el
  // cache de grupo).
  const videoStudioLock =
    isDevTester === false ? getModePlanLock("editor-video", plan) : null;

  // Popup "solo desktop": la card se muestra también en mobile (para que un
  // Ultra descubra la feature desde el celu), pero el editor pro solo corre en
  // pantallas grandes → al tocarla en mobile se le avisa que la use en la compu.
  const [showVideoDesktopOnly, setShowVideoDesktopOnly] = useState(false);

  const handleVideoStudioClick = () => {
    // No-Ultra (cualquier dispositivo): visor de ejemplos con nota del plan +
    // CTA "Mejorar plan"; sin ejemplos cargados, el modal clásico.
    if (videoStudioLock) {
      const studioExamples = getModeExamples("editor-video");
      if (studioExamples.length > 0) {
        setExamplesModal({
          title: "Estudio de Video",
          description:
            "Editá tus videos con IA — timeline, cortes y voces en un mismo lugar.",
          examples: studioExamples,
          actionLabel: "Mejorar plan",
          onAction: () => navigate("/subscription"),
          locked: true,
          planNote: planRequirementNote(videoStudioLock.feature),
        });
      } else {
        setModeLock(videoStudioLock);
      }
      return;
    }
    // Habilitado, pero en el celular: avisar que se usa desde la computadora
    // (mismo umbral que el resto del proyecto: < 768px = teléfono).
    if (window.matchMedia("(max-width: 767px)").matches) {
      setShowVideoDesktopOnly(true);
      return;
    }
    navigate("/editor-video");
  };

  return (
    <div className="px-4 sm:px-8 pt-2 pb-[calc(env(safe-area-inset-bottom,0px)+6rem)] md:pb-8 max-w-content mx-auto min-h-screen">
      {/* Título mobile (desktop usa el header de abajo) */}
      <MobilePageTitle>Modos</MobilePageTitle>
      {/* Desktop header */}
      <div className="hidden sm:flex items-center justify-between mb-5 h-11">
        <h1 className="text-[17px] font-semibold text-brand-black/90 mt-4">
          Modos
        </h1>
      </div>

      {/* Mobile: una columna apilada (como antes). Desktop: grid que usa el
          ancho — 2 columnas en pantallas medianas, 3 en anchas. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 sm:gap-x-6 gap-y-7 sm:gap-y-8">
        {/* Card "Plantillas" — primer elemento, SOLO desktop (`hidden sm:flex`).
            Lleva a la página de plantillas y reusa la misma imagen que el banner
            de plantillas de la home V2 desktop. Mismo estilo que las cards de
            modos (carátula 3:2 + pill + título/descripción) para un grid
            uniforme, pero con imagen estática en vez de video.

            No se muestra a las marcas de Hogar y Decoración: las plantillas son
            escenas con modelo y no aplican a muebles
            (ADR-2026-07-31-hogar-deco-sin-plantillas-ni-modelo). */}
        {!esHogarDeco && (
        <div
          onClick={() => navigate("/interfaz-v2/plantillas")}
          className="hidden sm:flex w-full text-left group cursor-pointer flex-col"
        >
          <div className="relative aspect-[3/2] bg-neutral-100 rounded-2xl overflow-hidden">
            <img
              src="/images/modo_automatico/modo_auto_caratula.png"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/interfaz-v2/plantillas");
              }}
              className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
            >
              <ArrowRight className="w-4 h-4" />
              Ver plantillas
            </button>
          </div>
          <div className="mt-3 flex flex-col gap-0.5">
            <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-brand-black/90 leading-tight transition-colors duration-300 sm:group-hover:text-brand-primary">
              Plantillas
            </h3>
            <p className="text-[13px] sm:text-[13.5px] text-[#3C3C43]/60 leading-snug">
              Explorá escenas listas para usar con tu producto.
            </p>
          </div>
        </div>
        )}
        {MODES.filter((m) => {
          // "Video con influencer" oculto para todos mientras el flag esté off.
          if (m.path === "video-influencer" && !VIDEO_INFLUENCER_ENABLED) return false;
          // Modos de muebles (catálogo + infografías): solo marcas de Hogar y
          // Decoración (ADR-2026-07-31-modos-muebles-por-rubro-de-marca).
          if (m.soloHogarDeco && !esHogarDeco) return false;
          // Inverso: "Generar imagen personalizada" (producto sobre un modelo
          // IA) no aplica a muebles y se oculta para esas marcas
          // (ADR-2026-07-31-hogar-deco-sin-plantillas-ni-modelo).
          if (m.ocultoEnHogarDeco && esHogarDeco) return false;
          // Modos devOnly solo para dev testers.
          return !m.devOnly || isDevTester === true;
        }).map((mode) => {
          // Lock por plan del modo (null si está permitido). Se usa para el
          // candado sobre la carátula y para el gating al tocar.
          const lock = getModePlanLock(mode.preset.kind, plan);
          // Ejemplos del modo (si no tiene, no se muestra "Ver ejemplos").
          const examples = getModeExamples(mode.preset.kind);
          // Navegamos con `presetData` en el state para que InterfazV2 abra el
          // sheet del modo en el primer paint (mecanismo `initialNavPreset`) y
          // NO se vea un flash de la home V2 al montar.
          const goToMode = () => {
            // Gating por plan: si el modo está bloqueado para el plan actual,
            // NO entramos — abrimos el visor de ejemplos (nota del plan +
            // CTA "Mejorar plan"); si el modo no tiene ejemplos cargados,
            // cae al modal clásico de "función exclusiva".
            if (lock) {
              if (examples.length > 0) {
                openExamples();
              } else {
                setModeLock(lock);
              }
              return;
            }
            navigate(`/interfaz-v2/modo/${mode.path}`, {
              state: { presetData: mode.preset, collectionPresets: [] },
            });
          };
          // Payload del visor de ejemplos (lo comparten el botón "Ver
          // ejemplos" y el tap sobre una card bloqueada).
          const openExamples = () =>
            setExamplesModal({
              title: mode.preset.title,
              description: mode.preset.description,
              examples,
              actionLabel: lock ? "Mejorar plan" : "Generar",
              onAction: lock ? () => navigate("/subscription") : goToMode,
              locked: !!lock,
              planNote: lock ? planRequirementNote(lock.feature) : undefined,
            });
          return (
            <div
              key={mode.path}
              onClick={goToMode}
              className="w-full text-left group cursor-pointer flex flex-col"
            >
              {/* Carátula en video, 4 esquinas redondeadas (igual que la card
                  de modo en la home V2). */}
              <div className="relative aspect-[3/2] bg-neutral-100 rounded-2xl overflow-hidden">
                {mode.video ? (
                  <video
                    ref={setupAutoplayVideo}
                    src={mode.video}
                    poster={mode.poster}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    aria-label={mode.preset.title}
                    className="absolute inset-0 w-full h-full object-cover sm:group-hover:scale-[1.03] transition-transform duration-500 ease-out"
                  />
                ) : (
                  // Modos sin video propio (catálogo-muebles / infografía):
                  // imagen estática de la card. Ver ADR-817.
                  <img
                    src={mode.preset.imageUrl}
                    alt={mode.preset.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover sm:group-hover:scale-[1.03] transition-transform duration-500 ease-out"
                  />
                )}
                {lock && <ModeLockOverlay />}
                {/* Botones sobre el video (abajo a la derecha): "Ver ejemplos"
                    (si el modo tiene ejemplos) + "Generar". Si el modo está
                    bloqueado, Generar se oculta (manda el candado + el modal) y
                    Ver ejemplos queda solo en su lugar — el z-[3] lo deja
                    tocable por encima del ModeLockOverlay (z-[2],
                    pointer-events-none). */}
                <div className="absolute bottom-3 right-3 z-[3] flex items-center gap-2">
                  {examples.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openExamples();
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/90 hover:bg-white backdrop-blur text-[#02062B] text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
                    >
                      <Images className="w-4 h-4" />
                      Ver ejemplos
                    </button>
                  )}
                  {!lock && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        goToMode();
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
                    >
                      <Sparkles className="w-4 h-4" />
                      Generar
                    </button>
                  )}
                </div>
              </div>
              {/* Texto debajo del video — sin chrome, igual que en la home. */}
              <div className="mt-3 flex flex-col gap-0.5">
                <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-brand-black/90 leading-tight transition-colors duration-300 sm:group-hover:text-brand-primary">
                  {mode.preset.title}
                </h3>
                <p className="text-[13px] sm:text-[13.5px] text-[#3C3C43]/60 leading-snug">
                  {mode.preset.description}
                </p>
              </div>
            </div>
          );
        })}

        {/* Card "Estudio de Video" — editor pro. Caso especial fuera de los
            modos V2 (igual que "Plantillas"). Se muestra en TODOS los tamaños:
            en desktop navega a /editor-video; en mobile un Ultra ve un aviso de
            "usalo desde la compu"; los no-Ultra ven el candado + modal Ver
            planes. Reusa ModeLockOverlay + FeatureLockedModal compartidos. */}
        <div
          onClick={handleVideoStudioClick}
          className="w-full text-left group cursor-pointer flex flex-col"
        >
          <div className="relative aspect-[3/2] bg-neutral-100 rounded-2xl overflow-hidden">
            <EstudioVideoCover />
            {videoStudioLock && <ModeLockOverlay />}
            {/* Botones sobre la carátula: "Ver ejemplos" (si hay cargados para
                'editor-video') + "Generar" (oculto si está bloqueado — ahí
                manda el candado + el modal; Ver ejemplos queda en su lugar). */}
            <div className="absolute bottom-3 right-3 z-[3] flex items-center gap-2">
              {getModeExamples("editor-video").length > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExamplesModal({
                      title: "Estudio de Video",
                      description:
                        "Editá tus videos con IA — timeline, cortes y voces en un mismo lugar.",
                      examples: getModeExamples("editor-video"),
                      actionLabel: videoStudioLock ? "Mejorar plan" : "Generar",
                      onAction: videoStudioLock
                        ? () => navigate("/subscription")
                        : handleVideoStudioClick,
                      locked: !!videoStudioLock,
                      planNote: videoStudioLock
                        ? planRequirementNote(videoStudioLock.feature)
                        : undefined,
                    });
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/90 hover:bg-white backdrop-blur text-[#02062B] text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
                >
                  <Images className="w-4 h-4" />
                  Ver ejemplos
                </button>
              )}
              {!videoStudioLock && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVideoStudioClick();
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
                >
                  <Sparkles className="w-4 h-4" />
                  Generar
                </button>
              )}
            </div>
          </div>
          <div className="mt-3 flex flex-col gap-0.5">
            <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-brand-black/90 leading-tight transition-colors duration-300 sm:group-hover:text-brand-primary">
              Estudio de Video
            </h3>
            <p className="text-[13px] sm:text-[13.5px] text-[#3C3C43]/60 leading-snug">
              Editá tus videos con IA — timeline, cortes y voces en un mismo lugar.
            </p>
          </div>
        </div>
      </div>

      <FeatureLockedModal
        open={modeLock !== null}
        onClose={() => setModeLock(null)}
        title={modeLock?.title ?? ""}
        message={modeLock?.message ?? ""}
      />

      <DesktopOnlyModal
        open={showVideoDesktopOnly}
        onClose={() => setShowVideoDesktopOnly(false)}
      />

      <ModeExamplesModal
        open={examplesModal !== null}
        onClose={() => setExamplesModal(null)}
        title={examplesModal?.title ?? ""}
        description={examplesModal?.description}
        examples={examplesModal?.examples ?? []}
        actionLabel={examplesModal?.actionLabel}
        onAction={examplesModal?.onAction}
        locked={examplesModal?.locked}
        planNote={examplesModal?.planNote}
      />
    </div>
  );
};
